import { CommunityRelationshipType, CustomListKey, IClient } from "@sp-crm/core";
import { NgBylinePill } from "components/byline/ng-byline-pill";
import React, { useMemo } from "react";
import { ResponsiveMode } from "store/reducers/responsive";
import { useCustomList } from "store/selectors/bridge";
import { useResponsiveMode } from "store/selectors/hooks";
import { Subpage } from ".";
import { ActionBarAssignedTo } from "./action-bar-assigned-to";
import { ActionBarDateComponent } from "./action-bar-date-component";
import { ActionBarMoveCommunity } from "./action-bar-move-community";
import { ActionBarStatus } from "./action-bar-status";
import { InvoiceSummary } from "./invoices/invoice-summary";

interface ShowClientActionBarProps {
    reactivate(): void;
    client: IClient;
    goToTab?: (section: Subpage) => void;
    hasActiveSubpage: boolean;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- eslintintroduction
    onFieldChange: (fieldName: string, newValue: any) => void;
}

export const ShowClientActionBar: React.FC<ShowClientActionBarProps> = props => {
    const { client, reactivate, goToTab, hasActiveSubpage, onFieldChange } = props;
    const responsiveMode = useResponsiveMode();
    const communities = useMemo(() => {
        const clientCommunities = client?.clientCommunities || [];
        return clientCommunities
            .filter(
                cc =>
                    cc.relationship === CommunityRelationshipType.featured ||
                    client.chosenCommunityId === cc.community?.id,
            )
            .map(cc => cc.community);
    }, [client?.clientCommunities, client?.chosenCommunityId]);

    const { getListItemKey } = useCustomList(CustomListKey.ClientStatus);

    const clientStatus = getListItemKey(client.statusListItemId);

    let scheduledClassName = "";

    switch (clientStatus) {
        case "moved":
        case "scheduled":
            scheduledClassName = " action-bar-accented-component";
            break;
    }

    const shouldShowActionBar =
        responsiveMode >= ResponsiveMode.large || !hasActiveSubpage;
    return (
        <div
            className={
                shouldShowActionBar
                    ? "opacity-100 max-h-96"
                    : "opacity-0 max-h-0 overflow-hidden"
            }>
            <div className="@container space-y-2 px-4 py-2">
                <div className="flex flex-wrap gap-2 md:gap-3">
                    <div>
                        <ActionBarStatus client={client} reactivate={reactivate} />
                    </div>
                    {clientStatus === "checkLater" ? (
                        <div>
                            <ActionBarDateComponent
                                className="check-back-later-date"
                                client={client}
                                datePropertyName="statusCheckLaterDate"
                                dateVal={client.statusCheckLaterDate}
                                displayLabel="Check Back Later Date"
                            />
                        </div>
                    ) : null}
                    <div>
                        <ActionBarDateComponent
                            client={client}
                            datePropertyName="moveTimeline"
                            dateVal={client.moveTimeline}
                            displayLabel="Estimated Move Timeline"
                        />
                    </div>
                    <div>
                        <ActionBarMoveCommunity
                            className={scheduledClassName}
                            client={client}
                            communities={communities}
                            goToTab={(section: Subpage) => goToTab(section)}
                        />
                    </div>
                    <div>
                        <ActionBarDateComponent
                            className={scheduledClassName}
                            client={client}
                            datePropertyName="moveTimelineActual"
                            dateVal={client.moveTimelineActual}
                            displayLabel="Move Date"
                        />
                    </div>
                    <InvoiceSummary clientId={client.id} />
                    <div>
                        <ActionBarAssignedTo client={client} />
                    </div>
                    <NgBylinePill
                        entity={client}
                        mode="action-bar"
                        createdAtDateModified={(newDate: Date) =>
                            onFieldChange("dateAddedManualOverride", newDate)
                        }
                    />
                </div>
            </div>
        </div>
    );
};
